import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, Mail, Zap, Trash2, Download, LogOut, RefreshCw, Search, Home, Building2, MessageCircle, Clock, CalendarDays, Target,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ASSETS } from "../lib/data";
import { AdminBlog } from "../components/admin/AdminBlog";
import { AdminFilms } from "../components/admin/AdminFilms";
import { AdminDistributors } from "../components/admin/AdminDistributors";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const STATUS = ["new", "contacted", "qualified", "won", "lost"];
const STATUS_COLOR = {
  new: "text-sky-400 border-sky-400/40 bg-sky-400/10",
  contacted: "text-amber-400 border-amber-400/40 bg-amber-400/10",
  qualified: "text-violet-400 border-violet-400/40 bg-violet-400/10",
  won: "text-lime2 border-lime2/40 bg-lime2/10",
  lost: "text-red-400 border-red-400/40 bg-red-400/10",
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("leads");
  const [wa, setWa] = useState(null);
  const [digest, setDigest] = useState(null);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([api.get("/admin/leads"), api.get("/admin/stats")]);
      setLeads(l.data);
      setStats(s.data);
      api.get("/admin/whatsapp/status").then((r) => setWa(r.data)).catch(() => {});
      api.get("/admin/digest/settings").then((r) => setDigest(r.data)).catch(() => {});
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("gesung_token");
        nav("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem("gesung_token")) {
      nav("/admin/login");
      return;
    }
    load();
  }, [load, nav]);

  const updateStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/leads/export`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("gesung_token")}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "gesung_leads.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const logout = () => {
    localStorage.removeItem("gesung_token");
    nav("/admin/login");
  };

  const sendDigest = async () => {
    try {
      const { data } = await api.post("/admin/digest/send");
      toast.success(data.count ? `Digest sent to ${data.to} (${data.count} leads)` : "No new leads in the last 24h");
    } catch { toast.error("Digest send failed"); }
  };

  const sendRollup = async () => {
    try {
      const { data } = await api.post("/admin/rollup/send");
      toast.success(`Weekly rollup sent to ${data.to} (${data.count} leads)`);
    } catch { toast.error("Rollup send failed"); }
  };

  const testWhatsapp = async () => {
    try {
      const { data } = await api.post("/admin/whatsapp/test");
      toast.success(`WhatsApp test sent (${data.sid})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "WhatsApp test failed");
    }
  };

  const saveDigest = async (hour, timezone) => {
    try {
      const { data } = await api.post("/admin/digest/settings", { hour, minute: 0, timezone });
      setDigest(data);
      toast.success(`Digest scheduled for ${String(hour).padStart(2, "0")}:00 ${timezone}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save digest time");
    }
  };

  const filtered = leads.filter((l) => {
    const matchQ = `${l.name} ${l.email} ${l.location} ${l.phone}`.toLowerCase().includes(query.toLowerCase());
    const matchF = filter === "all" || l.status === filter;
    return matchQ && matchF;
  });

  const chartData = stats
    ? STATUS.map((s) => ({ name: s, value: stats.by_status[s] || 0 }))
    : [];
  const barColors = ["#38bdf8", "#fbbf24", "#a78bfa", "#7CFC00", "#f87171"];

  const BUDGET_LABELS = { unsure: "Not sure", under_2k: "< $2k", "2k_5k": "$2–5k", "5k_15k": "$5–15k", "15k_plus": "$15k+" };
  const budgetData = stats
    ? ["under_2k", "2k_5k", "5k_15k", "15k_plus", "unsure"].map((k) => ({
        name: BUDGET_LABELS[k], value: stats.by_budget?.[k] || 0, high: k === "5k_15k" || k === "15k_plus",
      }))
    : [];

  const statCards = [
    { label: "Total Leads", value: stats?.total_leads ?? 0, icon: Zap },
    { label: "Subscribers", value: stats?.subscribers ?? 0, icon: Mail },
    { label: "Home", value: stats?.by_usecase?.home ?? 0, icon: Home },
    { label: "Business", value: stats?.by_usecase?.business ?? 0, icon: Building2 },
  ];

  const won = stats?.by_status?.won ?? 0;
  const lost = stats?.by_status?.lost ?? 0;
  const convRate = stats && stats.total_leads > 0 ? Math.round((won / stats.total_leads) * 100) : 0;
  const winRate = won + lost > 0 ? Math.round((won / (won + lost)) * 100) : 0;

  return (
    <div className="grain min-h-screen bg-charcoal text-offwhite" data-testid="admin-dashboard">
      {/* Topbar */}
      <header className="sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="Gesung" className="h-8 w-8 rounded-md object-cover" />
            <span className="font-display font-extrabold tracking-tight">GESUNG · Console</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="refresh-btn" className="grid place-items-center w-10 h-10 rounded-lg border border-white/10 hover:border-lime2 hover:text-lime2 transition-colors">
              <RefreshCw size={16} />
            </button>
            <button onClick={exportCsv} data-testid="export-btn" className="inline-flex items-center gap-2 rounded-lg border border-white/10 hover:border-lime2 hover:text-lime2 px-4 h-10 text-sm transition-colors">
              <Download size={15} /> CSV
            </button>
            <button onClick={sendDigest} data-testid="digest-btn" className="inline-flex items-center gap-2 rounded-lg border border-white/10 hover:border-lime2 hover:text-lime2 px-4 h-10 text-sm transition-colors">
              <Mail size={15} /> Digest
            </button>
            <button onClick={sendRollup} data-testid="rollup-btn" className="inline-flex items-center gap-2 rounded-lg border border-white/10 hover:border-lime2 hover:text-lime2 px-4 h-10 text-sm transition-colors">
              <CalendarDays size={15} /> Rollup
            </button>
            <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-2 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 px-4 h-10 text-sm transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-5 md:px-8 py-8">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8" data-testid="admin-tabs">
          {[{ id: "leads", label: "Leads" }, { id: "distributors", label: "Distributors" }, { id: "journal", label: "Journal" }, { id: "films", label: "Films" }].map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              data-testid={`tab-${tb.id}`}
              className={`text-sm font-medium rounded-full px-5 py-2.5 border transition-colors ${
                tab === tb.id ? "border-lime2 text-lime2 bg-lime2/10" : "border-white/15 text-white/60 hover:border-white/30"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {tab === "journal" ? (
          <AdminBlog />
        ) : tab === "films" ? (
          <AdminFilms />
        ) : tab === "distributors" ? (
          <AdminDistributors />
        ) : (
        <div data-testid="leads-panel">
        {/* WhatsApp alerts status */}
        {wa && (
          <div className={`mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border p-4 ${wa.configured ? "border-lime2/30 bg-lime2/5" : "border-amber-400/30 bg-amber-400/5"}`} data-testid="whatsapp-status">
            <div className="flex items-center gap-3">
              <MessageCircle size={18} className={wa.configured ? "text-lime2" : "text-amber-400"} />
              <div>
                <p className="text-sm font-medium">
                  WhatsApp alerts {wa.configured ? "enabled" : "not configured"}
                  <span className="text-white/40"> · high-value {wa.scope} quotes</span>
                </p>
                <p className="text-white/40 text-xs">
                  {wa.configured ? `Pinging ${wa.to} the moment a business quote arrives` : "Add your Twilio keys in backend .env to activate instant alerts"}
                </p>
              </div>
            </div>
            {wa.configured && (
              <button onClick={testWhatsapp} data-testid="whatsapp-test-btn" className="inline-flex items-center gap-2 rounded-lg border border-white/15 hover:border-lime2 hover:text-lime2 px-4 h-10 text-sm transition-colors self-start sm:self-auto">
                <MessageCircle size={15} /> Send test
              </button>
            )}
          </div>
        )}
        {/* Daily digest timing */}
        {digest && (
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#181818] p-4" data-testid="digest-settings">
            <div className="flex items-center gap-3">
              <Clock size={18} className="text-lime2" />
              <div>
                <p className="text-sm font-medium">Daily digest time</p>
                <p className="text-white/40 text-xs">
                  Emailed to {stats ? "your inbox" : "your inbox"} · next run {digest.next_run ? new Date(digest.next_run).toLocaleString() : "—"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(digest.hour)} onValueChange={(v) => saveDigest(Number(v), digest.timezone)}>
                <SelectTrigger data-testid="digest-hour" className="w-28 bg-charcoal border-white/15 h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {Array.from({ length: 24 }).map((_, h) => (
                    <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={digest.timezone} onValueChange={(v) => saveDigest(digest.hour, v)}>
                <SelectTrigger data-testid="digest-tz" className="w-44 bg-charcoal border-white/15 h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["UTC", "Africa/Accra", "Africa/Lagos", "Africa/Nairobi", "Africa/Johannesburg", "Africa/Dakar", "Africa/Cairo", "Europe/London"].map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {statCards.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className="rounded-xl border border-white/10 bg-[#181818] p-5"
                data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.2em] text-white/40">{c.label}</span>
                  <Icon size={16} className="text-emerald2" />
                </div>
                <p className="font-display text-4xl font-extrabold mt-3 text-lime2">{c.value}</p>
              </motion.div>
            );
          })}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-xl border border-lime2/30 bg-gradient-to-br from-lime2/10 to-transparent p-5"
            data-testid="stat-conversion"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.2em] text-white/40">Conversion</span>
              <Target size={16} className="text-lime2" />
            </div>
            <p className="font-display text-4xl font-extrabold mt-3 text-lime2" data-testid="conversion-rate">{convRate}%</p>
            <p className="text-[11px] text-white/40 mt-1">{won} won · {lost} lost · {winRate}% win rate</p>
          </motion.div>
        </div>

        {/* Charts */}
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <div className="rounded-xl border border-white/10 bg-[#181818] p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">Leads by status</p>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis dataKey="name" stroke="#666" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#666" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {chartData.map((_, i) => <Cell key={i} fill={barColors[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-[#181818] p-6" data-testid="budget-chart">
            <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">Leads by budget tier</p>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={budgetData} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" stroke="#666" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" stroke="#666" fontSize={11} tickLine={false} axisLine={false} width={80} />
                  <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {budgetData.map((d, i) => <Cell key={i} fill={d.high ? "#7CFC00" : "#00A86B"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search leads..." data-testid="search-input" className="bg-[#181818] border-white/10 h-11 pl-9" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="filter-status" className="w-full sm:w-44 bg-[#181818] border-white/10 h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="mt-4 rounded-xl border border-white/10 bg-[#181818] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="text-left text-white/40 text-xs uppercase tracking-[0.15em] border-b border-white/10">
                  <th className="px-5 py-4 font-medium">Name</th>
                  <th className="px-5 py-4 font-medium">Contact</th>
                  <th className="px-5 py-4 font-medium">Location</th>
                  <th className="px-5 py-4 font-medium">Use</th>
                  <th className="px-5 py-4 font-medium">Status</th>
                  <th className="px-5 py-4 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-5 py-12 text-center text-white/40">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-12 text-center text-white/40" data-testid="no-leads">No leads found.</td></tr>
                ) : filtered.map((l) => (
                  <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`lead-row-${l.id}`}>
                    <td className="px-5 py-4">
                      <p className="font-medium">{l.name}</p>
                      <p className="text-white/40 text-xs">{new Date(l.created_at).toLocaleDateString()}</p>
                    </td>
                    <td className="px-5 py-4 text-white/70">
                      <p>{l.email}</p>
                      <p className="text-white/40 text-xs">{l.phone}</p>
                    </td>
                    <td className="px-5 py-4 text-white/70">{l.location}</td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1 text-xs text-white/60 capitalize">
                        {l.use_case === "home" ? <Home size={13} /> : <Building2 size={13} />} {l.use_case}
                      </span>
                      {l.budget && l.budget !== "unsure" && (
                        <p className="text-[11px] mt-1 text-lime2">
                          {{ under_2k: "<$2k", "2k_5k": "$2–5k", "5k_15k": "$5–15k", "15k_plus": "$15k+" }[l.budget] || l.budget}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                        <SelectTrigger data-testid={`status-select-${l.id}`} className={`h-8 w-32 text-xs border capitalize ${STATUS_COLOR[l.status]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => remove(l.id)} data-testid={`delete-${l.id}`} className="text-white/30 hover:text-red-400 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>
        )}
      </main>
    </div>
  );
}
